/**
 * Tag utility functions for consistent tag handling across the application.
 *
 * Tags can come from the backend as either:
 * - Simple strings: "work", "personal"
 * - Objects with name property: { id: "123", name: "work", color: "#ff0000" }
 * - Objects with label property: { label: "work" }
 * - Objects with only id property: { id: "123" }
 *
 * These utilities provide type-safe coercion to string format.
 */

/**
 * Type representing a tag object with at least a name property.
 */
export interface TagObject {
  name: string;
  id?: string | number;
  color?: string;
  label?: string;
}

/**
 * Type representing a tag-like object that may have various properties.
 */
export interface TagLikeObject {
  name?: string;
  id?: string | number;
  label?: string;
  color?: string;
}

/**
 * Type representing a tag that could be either a string or an object.
 */
export type TagLike = string | TagObject | TagLikeObject | unknown;

/**
 * Type guard to check if a value is a string.
 *
 * @param tag - The value to check
 * @returns True if the tag is a string
 *
 * @example
 * if (isTagString(tag)) {
 *   // tag is narrowed to string type
 *   console.log(tag.toUpperCase());
 * }
 */
export function isTagString(tag: unknown): tag is string {
  return typeof tag === 'string';
}

/**
 * Type guard to check if a value is a tag object with a name property.
 *
 * @param tag - The value to check
 * @returns True if the tag is an object with a name property
 *
 * @example
 * if (isTagObject(tag)) {
 *   // tag is narrowed to TagObject type
 *   console.log(tag.name, tag.color);
 * }
 */
export function isTagObject(tag: unknown): tag is TagObject {
  return tag !== null && typeof tag === 'object' && 'name' in tag && typeof (tag as TagObject).name === 'string';
}

/**
 * Extracts the tag name from a tag that could be a string or an object.
 *
 * This function handles all the different formats tags can come in:
 * - String tags: returned as-is
 * - Object tags with name property: returns the name
 * - Object tags with label property (fallback): returns the label
 * - Object tags with id property (fallback): returns the id as string
 * - Any other value: converts to string
 *
 * @param tag - The tag to extract the name from (string, object, or unknown)
 * @returns The tag name as a string
 *
 * @example
 * // String tag
 * getTagName("work") // returns "work"
 *
 * // Object tag with name
 * getTagName({ id: "123", name: "work", color: "#ff0000" }) // returns "work"
 *
 * // Object tag with label (fallback)
 * getTagName({ label: "work" }) // returns "work"
 *
 * // Object tag with only id (fallback)
 * getTagName({ id: "123" }) // returns "123"
 *
 * // Unknown value
 * getTagName(123) // returns "123"
 */
export function getTagName(tag: TagLike): string {
  if (typeof tag === 'string') {
    return tag;
  }
  if (tag && typeof tag === 'object') {
    const tagObj = tag as TagLikeObject;
    // Priority: name > label > id
    if (typeof tagObj.name === 'string') {
      return tagObj.name;
    }
    if (typeof tagObj.label === 'string') {
      return tagObj.label;
    }
    if (tagObj.id !== undefined) {
      return String(tagObj.id);
    }
  }
  return String(tag);
}

/**
 * Extracts the tag name, returning null if unable to extract a valid string.
 *
 * This is a stricter version of getTagName that returns null instead of
 * converting unknown values to strings. Useful for filtering out invalid tags.
 *
 * @param tag - The tag to extract the name from
 * @returns The tag name as a string, or null if not extractable
 *
 * @example
 * getTagNameOrNull("work") // returns "work"
 * getTagNameOrNull({ name: "work" }) // returns "work"
 * getTagNameOrNull({}) // returns null
 * getTagNameOrNull(null) // returns null
 */
export function getTagNameOrNull(tag: TagLike): string | null {
  if (typeof tag === 'string') {
    return tag;
  }
  if (tag && typeof tag === 'object') {
    const tagObj = tag as TagLikeObject;
    if (typeof tagObj.name === 'string') {
      return tagObj.name;
    }
    if (typeof tagObj.label === 'string') {
      return tagObj.label;
    }
    if (tagObj.id !== undefined) {
      return String(tagObj.id);
    }
  }
  return null;
}

/**
 * Converts an array of mixed tag types to an array of tag name strings.
 *
 * @param tags - Array of tags (strings or objects)
 * @returns Array of tag name strings
 *
 * @example
 * getTagNames(["work", { name: "personal" }]) // returns ["work", "personal"]
 */
export function getTagNames(tags: TagLike[]): string[] {
  return tags.map(getTagName);
}

/**
 * Compares two tags for equality by their name.
 *
 * @param tagA - First tag to compare
 * @param tagB - Second tag to compare
 * @returns True if both tags have the same name
 *
 * @example
 * areTagsEqual("work", { name: "work" }) // returns true
 * areTagsEqual("work", "personal") // returns false
 */
export function areTagsEqual(tagA: TagLike, tagB: TagLike): boolean {
  return getTagName(tagA) === getTagName(tagB);
}
